import React, { useEffect, useRef, useState } from "react";
import { BookOpen, Plus, Trash2, Loader2, RefreshCw, ExternalLink, ImagePlus } from "lucide-react";
import { api } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const VOICES = [
  { id: "onyx", label: "Onyx (male, baritone)" },
  { id: "echo", label: "Echo (male, warm)" },
  { id: "shimmer", label: "Shimmer (female, warm)" },
  { id: "nova", label: "Nova (female, energetic)" },
];

const LANGS = ["english","spanish","hindi","gujarati","french","portuguese","german","italian","japanese","mandarin","arabic","russian","korean","turkish","dutch","polish","swahili"];
const CATEGORIES = ["novel","fiction","non-fiction","sci-fi","fantasy","mystery","thriller","romance","novella","short story","biography","self-help","business","history","poetry","kids"];

/**
 * Admin console for Fliboe Flip — indie audiobook platform.
 * Create a book, then add TTS-narrated chapters. Free chapters play publicly;
 * paid chapters require plan=chapters ($9.99/mo) once we wire the gate.
 */
export default function ChaptersCard() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [book, setBook] = useState({ title: "", author_name: "", description: "", language: "english", voice: "onyx", cover_url: "", category: "novel" });
  const [creating, setCreating] = useState(false);
  const [openBookId, setOpenBookId] = useState(null);
  const [chapter, setChapter] = useState({ title: "", text: "", is_free: true });
  const [addingCh, setAddingCh] = useState(false);
  const [err, setErr] = useState("");
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/books", { headers: authHeaders() });
      setBooks(data || []);
    } catch (_) { setBooks([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createBook = async () => {
    setErr("");
    if (!book.title.trim() || !book.author_name.trim()) { setErr("Title and author are required."); return; }
    setCreating(true);
    try {
      await api.post("/admin/books", book, { headers: authHeaders() });
      setBook({ title: "", author_name: "", description: "", language: "english", voice: "onyx", cover_url: "", category: "novel" });
      load();
    } catch (e) { setErr(e?.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };

  const addChapter = async (bookId) => {
    if (!chapter.title.trim() || !chapter.text.trim()) { setErr("Chapter title and text are required."); return; }
    setAddingCh(true); setErr("");
    try {
      await api.post(`/admin/books/${bookId}/chapters`, chapter, { headers: authHeaders(), timeout: 180000 });
      setChapter({ title: "", text: "", is_free: true });
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || (e?.code === "ECONNABORTED" ? "Narration is taking longer than expected. Try a shorter chapter or retry." : e?.message) || "Chapter add failed";
      setErr(msg);
    }
    finally { setAddingCh(false); }
  };

  const deleteBook = async (bookId, title) => {
    if (!window.confirm(`Delete "${title}" and all its chapters? Cannot be undone.`)) return;
    try { await api.delete(`/admin/books/${bookId}`, { headers: authHeaders() }); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };

  const deleteChapter = async (bookId, chId, title) => {
    if (!window.confirm(`Delete chapter "${title}"?`)) return;
    try { await api.delete(`/admin/books/${bookId}/chapters/${chId}`, { headers: authHeaders() }); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setErr("Cover image too large (max 8 MB)."); return; }
    setUploadingCover(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/images/upload", fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      setBook(b => ({ ...b, cover_url: data.url }));
    } catch (e) { setErr(e?.response?.data?.detail || "Cover upload failed"); }
    finally { setUploadingCover(false); }
  };

  return (
    <section className="fl-card p-6 mt-6" data-testid="chapters-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-400/30 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h2 className="fl-display text-xl font-bold text-white">Fliboe Flip — Audiobooks</h2>
            <p className="text-white/50 text-sm mt-0.5">Upload a book, then add TTS-narrated chapters in 16 languages. Public URL: <code className="text-emerald-300">/book/&lt;slug&gt;</code></p>
          </div>
        </div>
        <button onClick={load} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="chapters-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
        <a href="/publish" className="fl-btn !bg-emerald-500 !text-black hover:!bg-emerald-400 !py-1.5 !px-3 text-xs font-bold" data-testid="chapters-open-studio">
          Open Author Studio →
        </a>
      </div>

      {/* Sales editor lives on /publish — banner at top so admin can find it */}
      <div className="mb-4 p-3 rounded-lg border border-violet-400/30 bg-violet-500/5 text-xs text-violet-100" data-testid="chapters-studio-hint-top">
        📚 <strong>PDF upload, prices, bundle, sales link, author profile</strong> live in the Author Studio at{" "}
        <a href="/publish" className="text-emerald-300 underline hover:text-emerald-200 font-bold">/publish</a>{" "}
        — pick the book &rarr; open the <strong>Sales &amp; PDF</strong> tab.
      </div>

      {/* Create book */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input className="fl-input" placeholder="Book title" value={book.title} onChange={e => setBook({ ...book, title: e.target.value })} data-testid="chapters-book-title" />
        <input className="fl-input" placeholder="Author name" value={book.author_name} onChange={e => setBook({ ...book, author_name: e.target.value })} data-testid="chapters-book-author" />

        {/* Cover — upload only, no URL field */}
        <div className="md:col-span-2 flex gap-3 items-center">
          {book.cover_url ? (
            <img src={book.cover_url} alt="cover preview" className="w-20 h-28 object-cover rounded border border-white/10 shrink-0" data-testid="chapters-book-cover-preview" />
          ) : (
            <div className="w-20 h-28 rounded border border-dashed border-white/15 bg-white/5 flex items-center justify-center shrink-0">
              <ImagePlus className="w-5 h-5 text-white/30" />
            </div>
          )}
          <div className="flex-1">
            <input
              ref={coverInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => uploadCover(e.target.files?.[0])}
              data-testid="chapters-book-cover-file"
            />
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                disabled={uploadingCover}
                className="fl-btn ghost !py-1.5 !px-3 text-xs"
                data-testid="chapters-book-cover-upload"
              >
                {uploadingCover ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                {uploadingCover ? "Uploading…" : (book.cover_url ? "Change cover" : "Upload cover")}
              </button>
              {book.cover_url && (
                <button type="button" onClick={() => setBook({ ...book, cover_url: "" })} className="fl-btn ghost !py-1.5 !px-3 text-xs hover:!border-red-400/40 hover:!text-red-300" data-testid="chapters-book-cover-clear">
                  Remove
                </button>
              )}
            </div>
            <div className="text-white/40 text-[11px] mt-1.5">JPG, PNG, WEBP or GIF · up to 8 MB.</div>
          </div>
        </div>

        <textarea className="fl-input md:col-span-2" rows={2} placeholder="Book description / synopsis" value={book.description} onChange={e => setBook({ ...book, description: e.target.value })} data-testid="chapters-book-desc" />
        <select className="fl-input" value={book.language} onChange={e => setBook({ ...book, language: e.target.value })} data-testid="chapters-book-language">
          {LANGS.map(l => (<option key={l} value={l}>{l}</option>))}
        </select>
        <select className="fl-input" value={book.voice} onChange={e => setBook({ ...book, voice: e.target.value })} data-testid="chapters-book-voice">
          {VOICES.map(v => (<option key={v.id} value={v.id}>{v.label}</option>))}
        </select>
        <select className="fl-input md:col-span-2" value={book.category} onChange={e => setBook({ ...book, category: e.target.value })} data-testid="chapters-book-category">
          {CATEGORIES.map(c => (<option key={c} value={c}>{c}</option>))}
        </select>
      </div>
      <button onClick={createBook} disabled={creating} className="fl-btn mt-3" data-testid="chapters-create-book">
        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {creating ? "Creating…" : "Create book"}
      </button>
      {err && <div className="text-red-300 text-sm mt-2" data-testid="chapters-err">{err}</div>}

      {/* Book list */}
      <div className="mt-8 space-y-3">
        <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-2">Your books</div>
        {books.length === 0 ? (
          <div className="text-white/40 text-sm">No books yet. Create one above.</div>
        ) : books.map(b => (
          <div key={b.book_id} className="fl-card p-4" data-testid={`chapters-book-${b.book_id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              {b.cover_url ? (
                <img src={b.cover_url} alt="" className="w-14 h-20 object-cover rounded border border-white/10" />
              ) : (
                <div className="w-14 h-20 rounded bg-white/5 border border-white/10 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white/30" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-white font-bold truncate">{b.title}</div>
                <div className="text-white/50 text-xs">by {b.author_name} · {b.language} · {b.voice} · {(b.chapters || []).length} chapters</div>
                <a href={`/book/${b.slug}`} target="_blank" rel="noreferrer" className="text-violet-300 text-xs hover:underline inline-flex items-center gap-1 mt-1" data-testid={`chapters-book-public-${b.book_id}`}>
                  <ExternalLink className="w-3 h-3" /> /book/{b.slug}
                </a>
              </div>
              <button onClick={() => setOpenBookId(openBookId === b.book_id ? null : b.book_id)} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid={`chapters-toggle-${b.book_id}`}>
                {openBookId === b.book_id ? "Close" : "Chapters"}
              </button>
              <button onClick={() => deleteBook(b.book_id, b.title)} className="fl-btn ghost !py-1.5 !px-2.5 hover:!border-red-400/40 hover:!text-red-300" data-testid={`chapters-delete-${b.book_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>

            {openBookId === b.book_id && (
              <div className="mt-4 border-t border-white/5 pt-4">
                {(b.chapters || []).map(ch => (
                  <div key={ch.chapter_id} className="flex items-center gap-3 py-2 border-b border-white/5" data-testid={`chapters-ch-${ch.chapter_id}`}>
                    <div className="text-white/50 text-xs w-6">{ch.position}.</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm truncate">{ch.title}</div>
                      <div className="text-white/40 text-[11px]">{Math.round(ch.duration || 0)}s · {ch.is_free ? "free" : "paid"}</div>
                    </div>
                    <audio controls src={ch.audio_url} className="h-8" preload="none" />
                    <button onClick={() => deleteChapter(b.book_id, ch.chapter_id, ch.title)} className="fl-btn ghost !py-1 !px-2 hover:!border-red-400/40 hover:!text-red-300" data-testid={`chapters-ch-delete-${ch.chapter_id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}

                <div className="mt-3 space-y-2">
                  <input className="fl-input w-full" placeholder="Chapter title" value={chapter.title} onChange={e => setChapter({ ...chapter, title: e.target.value })} data-testid="chapters-ch-title" />
                  <textarea className="fl-input w-full" rows={5} placeholder="Paste chapter text — OpenAI TTS will narrate this in the book's voice + language." value={chapter.text} onChange={e => setChapter({ ...chapter, text: e.target.value })} data-testid="chapters-ch-text" />
                  <label className="flex items-center gap-2 text-sm text-white/70">
                    <input type="checkbox" checked={chapter.is_free} onChange={e => setChapter({ ...chapter, is_free: e.target.checked })} data-testid="chapters-ch-free" />
                    Free chapter (no paywall)
                  </label>
                  <button onClick={() => addChapter(b.book_id)} disabled={addingCh} className="fl-btn" data-testid={`chapters-add-ch-${b.book_id}`}>
                    {addingCh ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {addingCh ? "Narrating (up to 60s)…" : "Add chapter"}
                  </button>
                  {err && (
                    <div className="text-red-300 text-sm p-2 rounded bg-red-500/10 border border-red-400/30" data-testid={`chapters-inline-err-${b.book_id}`}>
                      ⚠ {err}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
